import Decimal from 'decimal.js';

import {
  DispositiveProvisionDispositionKind,
  TransferMethod,
} from '@/types/schema';
import { UnreachableError } from '@/utils/errors';
import {
  formatCurrency,
  formatCurrencyNoDecimals,
} from '@/utils/formatting/currency';
import { formatPercentSmart } from '@/utils/formatting/percent';
import { englishJoinStringList } from '@/utils/formatting/strings';

// ---------------------------------------------------------------------------
// Asset DP helpers
// ---------------------------------------------------------------------------

/**
 * Minimal shape of an asset DP provision for formatting purposes.
 * Compatible with `DispositiveProvisions_DispositiveProvisionFragment`.
 */
export interface AssetDPFields {
  dispositionPercentage?: Decimal | null;
  transferMethod: TransferMethod;
  assetGroups?: Array<{ name: string }> | null;
  assetStatics?: Array<{ name: string }> | null;
  insurancePolicies?: Array<{
    carrierName?: string | null;
    deathBenefitAmount?: Decimal | string | number | null;
  }> | null;
  ownershipStakes?: Array<{
    ownershipPercentage?: Decimal | null;
    ownedEntity?: { subtype: { displayName: string } } | null;
  }> | null;
}

/** Returns true when a provision has any associated asset edges. */
export function isAssetDP(provision: AssetDPFields): boolean {
  return (
    (provision.assetGroups?.length ?? 0) +
      (provision.assetStatics?.length ?? 0) +
      (provision.insurancePolicies?.length ?? 0) +
      (provision.ownershipStakes?.length ?? 0) >
    0
  );
}

export interface AssetCounts {
  insurancePolicies: number;
  assetGroups: number;
  assetStatics: number;
  ownershipStakes: number;
}

/** Extracts asset counts from a provision (GraphQL fragment shape). */
export function getProvisionAssetCounts(provision: AssetDPFields): AssetCounts {
  return {
    insurancePolicies: provision.insurancePolicies?.length ?? 0,
    assetGroups: provision.assetGroups?.length ?? 0,
    assetStatics: provision.assetStatics?.length ?? 0,
    ownershipStakes: provision.ownershipStakes?.length ?? 0,
  };
}

/** Returns true when insurance policies are the only asset type present. */
export function hasOnlyInsurancePolicies(counts: AssetCounts): boolean {
  return (
    counts.insurancePolicies > 0 &&
    counts.assetGroups === 0 &&
    counts.assetStatics === 0 &&
    counts.ownershipStakes === 0
  );
}

/**
 * Builds an ordered list of human-readable asset names from the provision's
 * asset edges. Order: ownership stakes, insurance policies, statics, groups.
 */
export function getAssetDisplayNames(provision: AssetDPFields): string[] {
  const names: string[] = [];

  provision.ownershipStakes?.forEach((stake) => {
    const entityName =
      stake.ownedEntity?.subtype.displayName ?? 'Unknown entity';
    names.push(entityName.trim());
  });

  provision.insurancePolicies?.forEach((policy) => {
    const name = (policy.carrierName || 'Life insurance').trim();
    if (policy.deathBenefitAmount) {
      const benefit = formatCurrencyNoDecimals(
        new Decimal(policy.deathBenefitAmount)
      );
      names.push(`${name} (${benefit} death benefit)`);
    } else {
      names.push(name);
    }
  });

  provision.assetStatics?.forEach((asset) => {
    names.push(asset.name.trim());
  });

  provision.assetGroups?.forEach((group) => {
    names.push(group.name.trim());
  });

  return names;
}

// ---------------------------------------------------------------------------
// Shared asset disposition label formatter
// ---------------------------------------------------------------------------

export interface AssetDispositionLabelInput {
  pct: Decimal;
  assetNames: string[];
  transferMethod: TransferMethod;
  isInsuranceOnly: boolean;
}

/**
 * Formats pre-resolved asset disposition data into a human-readable label.
 *
 * Examples:
 * - "100% of Property A"
 * - "100% of Property A, Property B, and Cash, in kind"
 * - "100% of Life Insurance Policy" (no suffix — insurance always in kind)
 */
export function formatAssetDispositionLabel(
  input: AssetDispositionLabelInput
): string {
  const assetNameList =
    englishJoinStringList(input.assetNames) || 'no assets selected';
  const pctText = `${formatPercentSmart(input.pct)}%`;

  let text = `${pctText} of ${assetNameList}`;

  // Insurance-only provisions always transfer in kind, so no suffix needed.
  if (!input.isInsuranceOnly) {
    if (input.transferMethod === TransferMethod.LiquidateToCash) {
      text += ', liquidate';
    } else {
      text += ', in kind';
    }
  }

  return text;
}

/**
 * Returns the label for the left column of an asset DP row.
 * Transfer method details are now shown in the right column via
 * `formatAssetDispositionLabel`, so this just returns "Asset".
 */
export function getTransferMethodLabel(_provision: AssetDPFields): string {
  return 'Asset';
}

// ---------------------------------------------------------------------------
// Entity-level DP formatting
// ---------------------------------------------------------------------------

export interface FormatDispositionKindCopyInput {
  dispositionKind: DispositiveProvisionDispositionKind;
  dispositionPercentage: Decimal | null;
  dispositionFormulaicPercentage?: Decimal | null;
  dispositionAmount: Decimal | null;
  dispositionLimit: Decimal | null;
  growByInflation: boolean;
}

interface FormatDispositionKindCopyOpts {
  /** Use no-decimal formatting for currency/percent */
  compact?: boolean;
}

/**
 * Formats a disposition kind into a human-readable string with the full set
 * of annotations: limit, inflation, and formula percentage prefix.
 */
export function formatDispositionKindCopy(
  input: FormatDispositionKindCopyInput,
  opts: FormatDispositionKindCopyOpts = {}
): string {
  const currencyFormatter = opts.compact
    ? formatCurrencyNoDecimals
    : formatCurrency;

  switch (input.dispositionKind) {
    case DispositiveProvisionDispositionKind.Percentage: {
      let text = `${formatPercentSmart(
        input.dispositionPercentage ?? new Decimal(0)
      )}%`;
      if (input.dispositionLimit) {
        text += `, limited to ${currencyFormatter(input.dispositionLimit)}`;
        // "inflation adjusted" only applies when there is a limit — the limit
        // is the dollar value that grows with inflation.
        if (input.growByInflation) {
          text += ', inflation adjusted';
        }
      }
      return text;
    }
    case DispositiveProvisionDispositionKind.Amount: {
      let text = currencyFormatter(input.dispositionAmount ?? new Decimal(0));
      if (input.growByInflation) {
        text += ', inflation adjusted';
      }
      return text;
    }
    case DispositiveProvisionDispositionKind.AnythingLeftOver: {
      // ALO has no discrete dollar value, so never append "inflation adjusted"
      const label = getFormulaLabel(input.dispositionKind);
      return prependFormulaPercentage(
        label,
        input.dispositionFormulaicPercentage ?? null
      );
    }
    case DispositiveProvisionDispositionKind.LesserOfRemainingStateOrFederalExemption:
    case DispositiveProvisionDispositionKind.RemainingGstExclusionOfGrantorInExcessOfLifetimeExclusion:
    case DispositiveProvisionDispositionKind.RemainingLifetimeExclusionOfGrantor:
    case DispositiveProvisionDispositionKind.RemainingFederalLifetimeExemptionInExcessOfStateExemption:
    case DispositiveProvisionDispositionKind.RemainingStateExemption:
    case DispositiveProvisionDispositionKind.RemainingGstExclusionOfGrantor: {
      // Exemption-based kinds have concrete dollar values that grow with inflation
      let text = prependFormulaPercentage(
        getFormulaLabel(input.dispositionKind),
        input.dispositionFormulaicPercentage ?? null
      );
      if (input.growByInflation) {
        text += ', inflation adjusted';
      }
      return text;
    }
    default:
      throw new UnreachableError({
        case: input.dispositionKind as never,
        message: `Unrecognized disposition kind: ${input.dispositionKind}`,
      });
  }
}

/** All disposition kinds except Percentage and Amount */
type FormulaDispositionKind = Exclude<
  DispositiveProvisionDispositionKind,
  | DispositiveProvisionDispositionKind.Percentage
  | DispositiveProvisionDispositionKind.Amount
>;

/**
 * Maps a formula-based disposition kind to its display label.
 */
function getFormulaLabel(kind: FormulaDispositionKind): string {
  switch (kind) {
    case DispositiveProvisionDispositionKind.AnythingLeftOver:
      return 'Anything left over';
    case DispositiveProvisionDispositionKind.LesserOfRemainingStateOrFederalExemption:
      return 'Lesser of remaining state or federal exemption';
    case DispositiveProvisionDispositionKind.RemainingGstExclusionOfGrantorInExcessOfLifetimeExclusion:
      return 'Remaining GST exemption in excess of federal lifetime exemption';
    case DispositiveProvisionDispositionKind.RemainingLifetimeExclusionOfGrantor:
      return 'Remaining lifetime exemption';
    case DispositiveProvisionDispositionKind.RemainingFederalLifetimeExemptionInExcessOfStateExemption:
      return 'Remaining federal lifetime exemption (in excess of state exemption)';
    case DispositiveProvisionDispositionKind.RemainingStateExemption:
      return 'Remaining state exemption';
    case DispositiveProvisionDispositionKind.RemainingGstExclusionOfGrantor:
      return 'Remaining GST exemption';
    default:
      throw new UnreachableError({
        case: kind,
        message: `Unrecognized formula disposition kind: ${kind}`,
      });
  }
}

/**
 * Prepends "x% of " to a formula name when the percentage is set and not 100%.
 * Lowercases the first character of the formula name when prepending.
 */
function prependFormulaPercentage(
  formulaName: string,
  dispositionPercentage: Decimal | null
): string {
  if (
    dispositionPercentage &&
    !dispositionPercentage.isZero() &&
    !dispositionPercentage.equals(100)
  ) {
    return `${formatPercentSmart(dispositionPercentage)}% of ${formulaName.charAt(0).toLowerCase() + formulaName.slice(1)}`;
  }
  return formulaName;
}
